import { QueryClient } from "@tanstack/solid-query";
import axios, { type CreateAxiosDefaults, isAxiosError } from "axios";
import localforage from "localforage";
import { handleRefreshToken } from "@/domains/auth/api";
import { isPlanLimitExceededError } from "@/domains/billing/utils/plan-limit";
import { buildLogoutUrlWithNext } from "@/libs/redirect";
import { tryCatch } from "@/libs/try-catch";

const baseConfig: CreateAxiosDefaults = {
  baseURL: `${import.meta.env.VITE_API_URL}`,
};

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";

let inMemoryToken: string | null = null;

export const setAuthToken = async (token: string) => {
  inMemoryToken = token;
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
  await localforage.setItem(ACCESS_TOKEN_KEY, token);
};

const removeAccessToken = async () => {
  inMemoryToken = null;
  axiosInstance.defaults.headers.common.Authorization = undefined;
  await localforage.removeItem(ACCESS_TOKEN_KEY);
};

const removeRefreshToken = async () => {
  axiosInstance.defaults.headers.common.Authorization = undefined;
  await localforage.removeItem(REFRESH_TOKEN_KEY);
};

export const removeAuthToken = async () => {
  inMemoryToken = null;
  axiosInstance.defaults.headers.common.Authorization = undefined;
  await Promise.all([removeAccessToken(), removeRefreshToken()]);
};

export const getAccessToken = async () => {
  if (inMemoryToken) return inMemoryToken;
  const [accessToken, error] = await tryCatch(
    localforage.getItem<string>(ACCESS_TOKEN_KEY),
  );
  if (error) return null;
  if (accessToken) inMemoryToken = accessToken;
  return accessToken;
};

export const setRefreshToken = async (token: string) => {
  return await localforage.setItem(REFRESH_TOKEN_KEY, token);
};

export const getRefreshToken = async () => {
  const [refreshToken, error] = await tryCatch(
    localforage.getItem<string>(REFRESH_TOKEN_KEY),
  );
  if (error) {
    return null;
  }
  return refreshToken;
};

export const axiosInstance = axios.create(baseConfig);

/**
 * Tetto all'attesa della pulizia dello storage prima della redirezione.
 *
 * `tryCatch` copre il *rifiuto*, non l'*attesa*: con IndexedDB bloccato da un
 * `versionchange` di un'altra scheda, o una transazione appesa, `removeItem`
 * non rifiuta e non risolve — e senza tetto `href` non verrebbe mai scritto.
 * Mezzo secondo è più di quanto serve a una cancellazione riuscita e meno di
 * quanto un utente noti; scaduto, la redirezione parte comunque ed è
 * `LogoutRoute` a ritentare la pulizia.
 */
const STORAGE_WIPE_TIMEOUT_MS = 500;

const afterTimeout = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Sessione non più recuperabile: via i token, poi a `/auth/logout` portando la
 * pagina di partenza.
 *
 * `await` sulla rimozione: scrive su IndexedDB e senza attenderla il cambio di
 * `location` può precederla — la pagina di logout ricarica con i token ancora
 * sul posto e ricade nello stesso 401. Ma l'attesa è dentro `tryCatch` **e**
 * sotto un tetto di tempo: se lo storage rifiuta (Safari privato, IndexedDB
 * bloccato, quota esaurita) o non risponde affatto, la redirezione parte
 * **comunque** — un errore di storage non deve né sopprimere l'uscita né
 * sostituire l'errore axios che il chiamante sta per ricevere.
 *
 * Solo `pathname`, mai `search`: le rotte che portano una credenziale in query
 * (`/verify-account?token=…`, `/accept-invitation?token=…`) sono proprio quelle
 * da cui torna chi ha il refresh token scaduto, e un `next` che le copiasse
 * intere metterebbe il token in un URL, quindi negli eventi e nei log. È la
 * stessa scelta di `buildLoginUrlWithNext`, e `ProtectedRoute` scarta comunque
 * la query un hop dopo.
 */
const clearSessionAndLeave = async () => {
  await Promise.race([
    tryCatch(removeAuthToken()),
    afterTimeout(STORAGE_WIPE_TIMEOUT_MS),
  ]);
  window.location.href = buildLogoutUrlWithNext(window.location.pathname);
};

/**
 * Il server ha **risposto** al refresh, e la risposta è "no": token scaduto,
 * revocato o malformato. Solo allora la sessione è davvero finita.
 *
 * Una rete caduta, un timeout o un 5xx non dicono nulla sul refresh token: era
 * valido per sette giorni un istante prima e lo è ancora. Distruggerlo per un
 * blip di rete — e navigare verso una pagina che offline non carica — sarebbe
 * un logout inflitto dalla connessione, non dal server.
 */
const isRefreshRejected = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  return status !== undefined && status >= 400 && status < 500;
};

/**
 * Il refresh in corso, condiviso da tutti i `401` concorrenti.
 *
 * `_retry` è una guardia **per richiesta**: impedisce che una singola richiesta rimbalzi
 * all'infinito, ma non dice nulla su due richieste diverse. Due `GET` autenticati che
 * partono insieme — la dashboard freelance ne monta esattamente due — prendevano `401`
 * insieme e chiedevano **due** refresh con lo stesso refresh token: con la rotazione, il
 * server accetta il primo e rifiuta il secondo, e il rifiuto è un 4xx, cioè un logout.
 * Un logout causato dall'aver aperto due query nello stesso istante.
 *
 * Chi arriva per primo crea la promise; gli altri aspettano quella e rigiocano con il
 * token nuovo. Le due scritture sono **dentro** la promise e attese: chi aspetta deve
 * trovare il token già persistito, non in volo. Il `finally` azzera il lock — senza, un
 * refresh fallito appenderebbe ogni richiesta successiva per sempre.
 */
let refreshInFlight: Promise<unknown> | null = null;

const refreshSessionOnce = (refreshToken: string) => {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const result = await handleRefreshToken(refreshToken);
        await setAuthToken(result.accessToken);
        await setRefreshToken(result.refreshToken);
        return result;
      } catch (error) {
        // La chiusura appartiene a chi ha fatto la chiamata: chi aspettava riceve lo
        // stesso rigetto e non la ripete. Irraggiungibile invece non chiude nulla (E3c).
        if (isRefreshRejected(error)) {
          await clearSessionAndLeave();
        }
        throw error;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
};

axiosInstance.interceptors.request.use(
  async (config) => {
    const accessToken = await getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
      config.withCredentials = true;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Add check to prevent infinite loops on refresh token failures
    if (
      error.response?.status === 401 &&
      originalRequest.url === `/refresh-token`
    ) {
      await clearSessionAndLeave();
      return Promise.reject(error);
    }

    const [refresh_token, missingTokenError] = await tryCatch(
      getRefreshToken(),
    );
    if (missingTokenError) {
      return Promise.reject(missingTokenError);
    }

    if (!refresh_token) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      // Rifiutato dal server: fuori (la chiusura la fa la promise condivisa).
      // Irraggiungibile: i token restano, il chiamante riceve l'errore di rete e la
      // sessione riprende al prossimo tentativo.
      const [, refreshTokenError] = await tryCatch(
        refreshSessionOnce(refresh_token as string),
      );
      if (refreshTokenError) {
        return Promise.reject(refreshTokenError);
      }
      // Il request interceptor rilegge il token in memoria, già aggiornato.
      return axiosInstance(originalRequest);
    }
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isPlanLimitExceededError(error)) {
      window.dispatchEvent(
        new CustomEvent("plan-limit-exceeded", { detail: error.response.data }),
      );
    }
    return Promise.reject(error);
  },
);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});
